import { Box, Button, FormControl, FormLabel, Input, Flex, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { type FormEvent, useCallback, useMemo, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import { useSnapshot } from 'valtio';

import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { JSONContentSchema } from '@duke-hq/shared-constants/tiptap';
import { Flag } from '@duke-hq/flags/react';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { useDukeFileUpload } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/blocknode/useDukeFileUpload';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { EditorMode } from '@duke-hq/shared-constants/assessments';

import { unitAssessmentState } from '../../UnitAssessmentState';
import { LinkedRequirementsSection } from '../LinkedRequirementsSection';

const DEFAULT_CONTENT: JSONContent = { type: 'doc', content: [] };

type FormEditorProps = {
  refetch: () => void;
};

export function FormEditor({ refetch }: FormEditorProps) {
  const { currentSlide, editorMode, currentAssessmentId, slidesLength, draftVersionId } =
    useSnapshot(unitAssessmentState);

  const { uploadFile } = useDukeFileUpload({
    fileAttributes: {
      attachedToKind: 'UNIT_ASSESSMENT',
      attachedToId: currentAssessmentId,
    },
  });

  const [title, setTitle] = useState(currentSlide?.title ?? '');
  const [descriptionEditorContent, setDescriptionEditorContent] = useState<JSONContent>();
  const [hintEditorContent, setHintEditorContent] = useState<JSONContent>();
  const [benchmarkEditorContent, setBenchmarkEditorContent] = useState<JSONContent>();

  const initialDescriptionContent = useMemo(() => {
    if (currentSlide && 'description' in currentSlide) {
      const parsedDescription = JSONContentSchema.safeParse(currentSlide.description);
      if (parsedDescription.success) {
        return parsedDescription.data;
      }

      return DEFAULT_CONTENT;
    }
    return DEFAULT_CONTENT;
  }, [currentSlide]);

  const initialHintContent = useMemo(() => {
    if (currentSlide && 'hint' in currentSlide) {
      const parsedHint = JSONContentSchema.safeParse(currentSlide.hint);
      if (parsedHint.success) {
        return parsedHint.data;
      }

      return DEFAULT_CONTENT;
    }

    return DEFAULT_CONTENT;
  }, [currentSlide]);

  const initialBenchmarkContent = useMemo(() => {
    if (currentSlide && 'benchmarkAnswer' in currentSlide && currentSlide.benchmarkAnswer != null) {
      const parsedBenchmark = JSONContentSchema.safeParse(currentSlide.benchmarkAnswer);
      if (parsedBenchmark.success) {
        return parsedBenchmark.data;
      }

      return DEFAULT_CONTENT;
    }

    return DEFAULT_CONTENT;
  }, [currentSlide]);

  const createFormSlideMutation = trpcAdmin.unitAssessment.createFormSlide.useMutation({
    onSuccess: (newSlide) => {
      unitAssessmentState.setNewlyCreatedSlideId(newSlide.id);
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });

  const updateFormSlideMutation = trpcAdmin.unitAssessment.updateFormSlide.useMutation({
    onSuccess: () => {
      unitAssessmentState.closeEditor();
      unitAssessmentState.setHasUnpublishedChanges(true);
      refetch();
    },
  });

  const handleSave = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const payload = {
        title,
        description: descriptionEditorContent ?? initialDescriptionContent,
        hint: hintEditorContent ?? initialHintContent,
        benchmarkAnswer: benchmarkEditorContent ?? initialBenchmarkContent,
      };

      if (editorMode === EditorMode.CREATE) {
        await createFormSlideMutation.mutateAsync({
          ...payload,
          assessmentId: currentAssessmentId,
          unitAssessmentVersionId: draftVersionId,
        });
      } else if (editorMode === EditorMode.EDIT && currentSlide) {
        await updateFormSlideMutation.mutateAsync({ id: currentSlide.id, ...payload });
      }
    },
    [
      title,
      descriptionEditorContent,
      initialDescriptionContent,
      hintEditorContent,
      initialHintContent,
      benchmarkEditorContent,
      initialBenchmarkContent,
      createFormSlideMutation,
      updateFormSlideMutation,
      currentSlide,
      editorMode,
      currentAssessmentId,
      slidesLength,
      draftVersionId,
    ],
  );

  return (
    <form onSubmit={handleSave}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel htmlFor="form-title">
            <Trans>Title</Trans>
          </FormLabel>
          <Input
            id="form-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t`Enter the form title`}
          />
        </FormControl>

        <LinkedRequirementsSection />

        <FormControl>
          <FormLabel htmlFor="form-description">
            <Trans>Description</Trans>
          </FormLabel>
          <Box
            w="100%"
            borderWidth="1px"
            borderRadius="md"
            outline="1px"
            boxShadow="sm"
            minH="200px"
          >
            <DukeTiptapEditor
              initialContent={initialDescriptionContent}
              onChange={(content) => setDescriptionEditorContent(content)}
              uploadFn={uploadFile}
            />
          </Box>
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="form-hint">
            <Trans>Hint</Trans>
          </FormLabel>
          <Box
            w="100%"
            borderWidth="1px"
            borderRadius="md"
            outline="1px"
            boxShadow="sm"
            minH="150px"
          >
            <DukeTiptapEditor
              initialContent={initialHintContent}
              onChange={(content) => setHintEditorContent(content)}
              uploadFn={uploadFile}
            />
          </Box>
        </FormControl>

        <Flag.Boolean
          flagKey="BENCHMARK_ANSWER_RELEASE"
          whenTrue={
            <FormControl>
              <FormLabel>
                <Trans>Benchmark answer</Trans>
              </FormLabel>
              <Box w="100%" borderWidth="1px" borderRadius="md" boxShadow="sm" minH={pxToRem(150)}>
                <DukeTiptapEditor
                  initialContent={initialBenchmarkContent}
                  onChange={(content) => setBenchmarkEditorContent(content)}
                  uploadFn={uploadFile}
                />
              </Box>
            </FormControl>
          }
          whenFalse={<span />}
        />

        <Flex justify="flex-end">
          <Button right={0} type="submit">
            {editorMode === EditorMode.EDIT ? <Trans>Update</Trans> : <Trans>Add</Trans>}
          </Button>
        </Flex>
      </VStack>
    </form>
  );
}
